import * as React from 'react';
import { cn } from 'cn';

/**
 * Spinner — o indicador de processamento. Diz que algo está acontecendo, não quanto falta; para isso é o `Progress`.
 *
 * Anatomia: um anel de trilha na cor suave da marca e um arco fixo (~30% da volta) na cor da
 * marca, que gira (`--animate-spinner-spin` em `styles.css`), com um rótulo opcional à
 * direita, a 8px.
 *
 * O giro é só `transform` num elemento HTML com `will-change: transform`, para rodar no
 * compositor: o spinner aparece justamente quando a thread principal está ocupada (carregando
 * módulos, montando telas), e uma animação de `stroke-dasharray` — que só roda na thread
 * principal — parava nesses intervalos e fazia o giro engasgar. Os tamanhos são os oito da especificação, e o rótulo acompanha:
 *
 *   extra-tiny 16 · tiny 20 · extra-small 24 · small 28     rótulo 14/20 regular
 *   medium 32 (padrão) · large 36 · extra-large 40           rótulo 16/22 semibold
 *   huge 44                                                  rótulo 20/28 semibold
 *
 * A espessura do traço sobe com o tamanho (1.5 → 2 → 3 → 4), como no kit.
 *
 * `variant="inverted"` é o "Subtle" da especificação: sobre fundo da marca, o arco e o rótulo
 * ficam brancos. Cor de trilha e de arco são papéis (`--spinner-track`, `--spinner-arc`).
 *
 * Acessibilidade: é um `role="status"`. Sem rótulo visível, `aria-label` é obrigatório — o
 * componente exige a palavra, mas não a escolhe. Com rótulo, ele já é o texto.
 */

const SPINNER_SIZES = {
  'extra-tiny': 16,
  tiny: 20,
  'extra-small': 24,
  small: 28,
  medium: 32,
  large: 36,
  'extra-large': 40,
  huge: 44,
} as const;

export type SpinnerSize = keyof typeof SPINNER_SIZES;

function strokeFor(px: number): number {
  if (px <= 24) return 1.5;
  if (px <= 36) return 2;
  if (px <= 40) return 3;
  return 4;
}

function labelClassFor(px: number): string {
  if (px <= 28) return 'type-body1';
  if (px <= 40) return 'type-subtitle2';
  return 'type-subtitle1';
}

type SpinnerProps = Omit<React.ComponentProps<'span'>, 'children'> & {
  size?: SpinnerSize;
  variant?: 'primary' | 'inverted';
  /** O que está acontecendo ("Salvando…"). Vira o texto anunciado. */
  label?: React.ReactNode;
  /** Onde o rótulo fica em relação ao anel. */
  labelPosition?: 'after' | 'below';
} & (
    { label: React.ReactNode; 'aria-label'?: string } | { label?: undefined; 'aria-label': string }
  );

function Spinner({
  size = 'medium',
  variant = 'primary',
  label,
  labelPosition = 'after',
  className,
  ...props
}: SpinnerProps) {
  const px = SPINNER_SIZES[size];
  const stroke = strokeFor(px);
  // O desenho vive num viewBox de 32; o traço é dado em px de tela, então o raio desconta
  // a metade dele convertida para a escala do viewBox — o anel nunca é cortado na borda.
  const strokeUnits = (stroke * 32) / px;
  const radius = 16 - strokeUnits / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <span
      role="status"
      data-slot="spinner"
      data-size={size}
      data-variant={variant}
      className={cn(
        'inline-flex items-center gap-2',
        labelPosition === 'below' && 'flex-col',
        variant === 'inverted' ? 'text-white' : 'text-foreground',
        className,
      )}
      {...props}
    >
      <span aria-hidden className="animate-spinner-spin inline-flex shrink-0 will-change-transform">
        <svg viewBox="0 0 32 32" width={px} height={px} fill="none">
          <circle
            cx="16"
            cy="16"
            r={radius}
            strokeWidth={strokeUnits}
            className={variant === 'inverted' ? 'stroke-white/40' : 'stroke-spinner-track'}
          />
          <circle
            cx="16"
            cy="16"
            r={radius}
            strokeWidth={strokeUnits}
            strokeLinecap="round"
            strokeDasharray={`${circumference * 0.3} ${circumference}`}
            className={variant === 'inverted' ? 'stroke-white' : 'stroke-spinner-arc'}
          />
        </svg>
      </span>
      {label !== undefined && (
        <span className={cn(labelClassFor(px), 'whitespace-nowrap')}>{label}</span>
      )}
    </span>
  );
}

export { Spinner, SPINNER_SIZES };
